import React, {ReactNode} from "react";
import {Button} from "primereact/button";
import Exception from "../../../../shared/exception/Exception";
import {Dialog} from "primereact/dialog";
import CertificateInput, {CertificateInputType} from "../../../../shared/components/CertificateInput";
import PortalContext from "../../../PortalContext";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import {CertificateEntry} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export interface CertificateEncryptAddDialogProperties extends AbstractDialogProperties {
	context: PortalContext;
	exception?: Exception;
	submit: (certificate: CertificateEntry) => void;
}

export interface CertificateEncryptAddDialogState extends AbstractDialogState {
	certificate?: CertificateEntry;
}

export class CertificateEncryptAddDialogDefinition
	extends AbstractDialog<CertificateEncryptAddDialogProperties, CertificateEncryptAddDialogState> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<CertificateEncryptAddDialogProperties>) {
		super(props);

		this.hide = this.hide.bind(this);
		this.submit = this.submit.bind(this);
		this.onChange = this.onChange.bind(this);
		this.renderError = this.renderError.bind(this);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();
		this.props.submit(this.state.certificate!);
		this.hide();
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.setState(
			{
				certificate: undefined
			}
		);

		super.hide();
	}

	/**
	 * Render dialog errors
	 */
	protected renderError(): ReactNode {
		let error: Exception | undefined = FunctionHelper.applyValue(this.state.exception, this.props.exception);
		return super.renderError(error);
	}

	/**
	 * Render the dialog header
	 */
	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-unlocked small"}/>
					<Translate {...this.props} data="certificate_encrypt.add.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 * @protected
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.add")}
							disabled={
								!FunctionHelper.exists(this.state.certificate) ||
								FunctionHelper.exists(this.state.exception) || this.state.submitted
							}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-add")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted && FunctionHelper.exists(this.state.certificate) &&
			!FunctionHelper.exists(this.state.exception);
	}

	protected renderDialog(): ReactNode {
		let certificate: x509.X509Certificate | undefined;
		if (FunctionHelper.exists(this.state.certificate)) {
			try {
				certificate = new x509.X509Certificate(this.state.certificate!.certificate);
			} catch (e: any) {
				// can be ignored here
			}
		}

		let classList: Array<string> = ["certificate-encrypt-add-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.certificate.add"} namespace={"help"} markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<label className="p-col-12 label-left">
								<Translate {...this.props} data="certificate_encrypt.add.certificate" namespace="dialog"
										   values={{
											   certificate: FunctionHelper.exists(this.state.exception) ?
												   "" : FunctionHelper.applyValue(certificate?.subject, "")
										   }}/>
							</label>
							<div className="p-col-12 p-grid">
								<CertificateInput {...this.props} inputId="control-certificate"
												  type={CertificateInputType.CERTIFICATE}
												  value={FunctionHelper.applyValue(
													  this.state.certificate?.certificate, ""
												  )}
												  onChange={(value) => this.onChange(value)}/>
							</div>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	private onChange(certificateEntry: CertificateEntry): void {
		let exception: Exception | undefined;

		if (FunctionHelper.isEmpty(certificateEntry.certificate)) {
			exception = new Exception(Translate.value("errors.fields.set", {ns: "message"}));
		} else {
			try {
				let certificate: x509.X509Certificate = new x509.X509Certificate(certificateEntry.certificate);

				if (certificate.notAfter < new Date()) {
					exception = new Exception(
						Translate.value("errors.fields.certificate.date", {ns: "message"})
					);
				}
			} catch (e: any) {
				exception = new Exception(
					Translate.value("errors.fields.certificate.invalid", {ns: "message"})
				);
			}
		}

		this.setState({
			certificate: certificateEntry,
			exception: exception
		});
	}
}

const CertificateEncryptAddDialog = React.memo(CertificateEncryptAddDialogDefinition,
	(
		prevProps: Readonly<CertificateEncryptAddDialogProperties>,
		nextProps: Readonly<CertificateEncryptAddDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default CertificateEncryptAddDialog;